import type { SiteLang } from "@/hooks/useLanguage";
import type { OfferingSection } from "@/components/services/GenericOfferingContent";

/* ─────────────────────────────────────────────────────
   Spanish translations for QualValOffering page metadata
   ───────────────────────────────────────────────────── */

interface OfferingPageMeta {
  title: string;
  subtitle: string;
  description: string;
  faqItems?: { q: string; a: string }[];
}

const offeringMetaES: Record<string, OfferingPageMeta> = {
  commissioning: {
    title: "Commissioning",
    subtitle: "Servicios de commissioning a medida para un rendimiento y seguridad óptimos",
    description:
      "El commissioning es fundamental para que tus sistemas y equipos funcionen al máximo rendimiento y con total seguridad en cualquier industria. En QbD Group somos expertos en asegurar que tus sistemas, utilities, instalaciones y equipos estén en marcha y funcionando correctamente.",
  },
  "facility-qualification": {
    title: "Cualificación de instalaciones",
    subtitle: "Valida tus instalaciones para garantizar fiabilidad, eficiencia y cumplimiento normativo",
    description:
      "En QbD Group ofrecemos servicios de cualificación de instalaciones adaptados a tu industria y requisitos regulatorios específicos. Al asociarte con nosotros, puedes confiar en que tus instalaciones y utilities están validadas según los más altos estándares, garantizando la fiabilidad, eficiencia y seguridad de tus operaciones.",
  },
  "utility-qualification": {
    title: "Cualificación de utilities",
    subtitle: "Valida tus utilities para garantizar cumplimiento, fiabilidad y eficiencia",
    description:
      "En QbD Group ofrecemos servicios de cualificación de utilities adaptados a tu industria y requisitos regulatorios específicos. Asóciate con QbD Group para asegurar que tus utilities están validadas según los más altos estándares, garantizando fiabilidad, eficiencia y seguridad en tus operaciones.",
  },
  "equipment-qualification": {
    title: "Cualificación de equipos",
    subtitle: "Soluciones flexibles y personalizadas para la cualificación de equipos",
    description:
      "¿Buscas una solución llave en mano para la parte de C&Q y cualificación de equipos de tu proyecto? Ya sea que estés construyendo o renovando y necesites cualificar tus equipos, ¡estamos aquí para ayudarte! Desde equipos sencillos hasta sistemas más complejos, QbD Group ofrece soluciones flexibles y personalizadas para cubrir tus necesidades.",
  },
  "process-validation": {
    title: "Validación de procesos",
    subtitle: "Asegura la calidad de tus productos y el cumplimiento normativo con procesos validados",
    description:
      "¿Necesitas experiencia en validación de procesos? La validación de procesos es un componente crítico para mantener la calidad del producto, el cumplimiento normativo y la excelencia operativa en la fabricación. Al asociarte con QbD Group, puedes confiar en que tus procesos están validados según los más altos estándares.",
  },
  "cleaning-validation": {
    title: "Validación de limpieza",
    subtitle: "Asegura una fabricación segura y conforme con procesos de limpieza validados",
    description:
      "QbD ofrece una amplia gama de servicios de cualificación y validación para tus procesos, aplicaciones y equipos. Nuestros expertos poseen un conocimiento profundo en validación de limpieza, asegurando que tus operaciones cumplan los más altos estándares.",
  },
  "analytical-method-validation": {
    title: "Validación de métodos analíticos",
    subtitle: "Valida tus métodos analíticos para garantizar precisión, cumplimiento y fiabilidad",
    description:
      "Asegura que tus productos farmacéuticos cumplen los más altos estándares regulatorios con nuestros servicios integrales de validación de métodos analíticos. Nos especializamos en ofrecer soluciones precisas, fiables y conformes para ayudarte a validar los métodos analíticos críticos para el control de calidad.",
  },
  "sterilization-validation": {
    title: "Validación de esterilización",
    subtitle: "Asegura la seguridad del producto y el cumplimiento normativo con procesos de esterilización validados",
    description:
      "Nos especializamos en servicios integrales de validación de esterilización adaptados a tu industria y requisitos regulatorios específicos. La validación de esterilización es un componente crítico para garantizar la seguridad del producto, el cumplimiento normativo y la confianza del consumidor.",
  },
  "tech-transfer": {
    title: "Transferencia tecnológica",
    subtitle: "Agiliza tu proceso de transferencia tecnológica para mayor eficiencia y cumplimiento",
    description:
      "La transferencia tecnológica es un proceso vital para trasladar la innovación a aplicaciones reales, impulsar el crecimiento económico y fomentar la colaboración y la innovación. Nuestro experimentado equipo de QbD Group ofrece soporte integral, desde la evaluación tecnológica y la documentación hasta la optimización de procesos y la formación.",
  },
  "process-product-support": {
    title: "Soporte de procesos y productos",
    subtitle: "Optimiza tus procesos y mejora la calidad de tus productos con soporte experto",
    description:
      "Nuestro experimentado equipo ofrece experiencia en optimización de procesos, gestión de la calidad, cumplimiento normativo y metodologías de mejora continua para ayudarte a alcanzar la excelencia operativa y de producto.",
  },
};

const offeringMetaZH: Record<string, OfferingPageMeta> = {
  commissioning: {
    title: "Commissioning",
    subtitle: "量身定制的 commissioning 服务，实现最佳性能与安全",
    description:
      "Commissioning 对于让您的系统和设备在各行各业中实现最佳性能并保持安全至关重要。在 QbD Group，我们是确保您的系统、utilities、设施和设备正常运行的专家。",
  },
  "analytical-method-validation": {
    title: "分析方法验证",
    subtitle: "验证您的分析方法，确保准确性、合规性与可靠性",
    description:
      "通过我们全面的分析方法验证服务，确保您的药品产品达到最高的法规标准。我们专注于提供精准、可靠且合规的解决方案，帮助您验证对质量控制至关重要的分析方法。",
  },
  "cleaning-validation": {
    title: "清洁验证",
    subtitle: "通过经过验证的清洁流程，确保安全合规的生产",
    description:
      "QbD 为您的流程、应用和设备提供广泛的确认与验证服务。我们的专家在清洁验证方面拥有深厚的专业知识，确保您的运营符合最高标准。",
  },
  "equipment-qualification": {
    title: "设备确认",
    subtitle: "灵活定制的设备确认解决方案",
    description:
      "您是否在为项目中的 C&Q 与设备确认部分寻找交钥匙解决方案？无论您是在新建还是改造，并需要对设备进行确认，我们都能为您提供帮助！从简单设备到更复杂的系统，QbD Group 提供灵活且量身定制的解决方案，满足您的需求。",
  },
  "facility-qualification": {
    title: "设施确认",
    subtitle: "对您的设施进行验证，确保可靠性、效率与合规性",
    description:
      "在 QbD Group，我们提供根据您所在行业及具体法规要求量身定制的设施确认服务。与我们合作，您可以确信您的设施和 utilities 已按最高标准完成验证，从而保障运营的可靠性、效率与安全。",
  },
  "process-product-support": {
    title: "工艺与产品支持",
    subtitle: "通过专家支持优化流程并提升产品质量",
    description:
      "我们经验丰富的团队在工艺优化、质量管理、法规合规以及持续改进方法方面拥有专业知识，帮助您实现卓越的运营与产品表现。",
  },
  "process-validation": {
    title: "工艺验证",
    subtitle: "通过经过验证的工艺确保产品质量与法规合规",
    description:
      "您是否在寻找工艺验证方面的专业能力？工艺验证是维护产品质量、法规合规与生产卓越运营的关键环节。与 QbD Group 合作，您可以确信您的工艺已按最高标准完成验证。",
  },
  "sterilization-validation": {
    title: "灭菌验证",
    subtitle: "通过经过验证的灭菌工艺确保产品安全与法规合规",
    description:
      "我们专注于提供根据您所在行业及具体法规要求量身定制的全面灭菌验证服务。灭菌验证是确保产品安全、法规合规与消费者信任的关键环节。",
  },
  "tech-transfer": {
    title: "技术转移",
    subtitle: "简化您的技术转移流程，提升效率与合规性",
    description:
      "技术转移是将创新转化为实际应用、推动经济增长、促进协作与创新的关键环节。我们经验丰富的 QbD Group 团队提供全面的支持，涵盖从技术评估与文档管理到工艺优化与培训的各个方面。",
  },
  "utility-qualification": {
    title: "Utility 确认",
    subtitle: "验证您的 utilities，确保合规性、可靠性与效率",
    description:
      "在 QbD Group，我们提供根据您所在行业及具体法规要求量身定制的 utility 确认服务。与 QbD Group 合作，确保您的 utilities 按最高标准完成验证，保障运营的可靠性、效率与安全。",
  },
};

export const getQVOfferingMeta = (contentKey: string, lang: SiteLang): OfferingPageMeta | undefined => {
  if (lang === "es") return offeringMetaES[contentKey];
  if (lang === "zh-cn") return offeringMetaZH[contentKey];
  return undefined;
};

/* ─────────────────────────────────────────────────────
   Spanish translations for detailed Q&V offering content
   ───────────────────────────────────────────────────── */

type OfferingSectionTextOverride = {
  whatTitle: string;
  whatParagraphs: string[];
  whyTitle: string;
  whyIntro?: string;
  whyBenefits: { title: string; text: string }[];
  processTitle?: string;
  processIntro?: string;
  processSteps?: { title: string; text: string }[];
  partnerTitle: string;
  partnerIntro: string;
  partnerBullets: string[];
};

const offeringContentES: Record<string, OfferingSectionTextOverride> = {
  "utility-qualification": {
    whatTitle: "¿Qué es la cualificación de utilities?",
    whatParagraphs: [
      "La cualificación de utilities es el proceso de validar y asegurar que los sistemas de servicios auxiliares, como agua, vapor, aire y gas, utilizados en la fabricación farmacéutica cumplen los estándares requeridos de seguridad, fiabilidad y rendimiento.",
      "En la industria farmacéutica, las utilities a menudo se consideran parte del cumplimiento de las Buenas Prácticas de Fabricación (GMP) y deben cualificarse cuidadosamente para garantizar que no afectan negativamente a la calidad del producto ni lo contaminan.",
      "Los sistemas de utilities clave que requieren cualificación incluyen: <strong class='text-foreground'>Sistemas de agua</strong> (agua purificada, agua para inyección), <strong class='text-foreground'>Sistemas de aire y gas</strong> (aire comprimido, nitrógeno, oxígeno), <strong class='text-foreground'>Sistemas HVAC</strong> y <strong class='text-foreground'>Sistemas de vapor</strong>.",
    ],
    whyTitle: "¿Por qué es importante la cualificación de utilities?",
    whyIntro: "Los sistemas de utilities desempeñan un papel vital en la producción farmacéutica. Estas son las razones por las que la cualificación es esencial:",
    whyBenefits: [
      { title: "Cumplimiento normativo", text: "Garantiza el cumplimiento de los estándares GMP y evita interrupciones durante las inspecciones." },
      { title: "Seguridad y calidad del producto", text: "Mantiene la integridad del producto previniendo la contaminación y asegurando que las utilities cumplen los estándares de rendimiento." },
      { title: "Ahorro de costes", text: "Reduce el riesgo de fallos, tiempos de inactividad e incidentes de seguridad, minimizando interrupciones costosas." },
      { title: "Eficiencia operativa", text: "Optimiza el rendimiento de los sistemas de utilities, reduciendo el desperdicio y mejorando la fiabilidad." },
    ],
    processTitle: "Nuestro proceso",
    processIntro: "QbD Group ofrece servicios flexibles y escalables de cualificación de utilities adaptados a los requisitos de tu proyecto. Ya necesites una solución llave en mano, donde QbD gestiona todo el proyecto desde la planificación y ejecución hasta la documentación, o un enfoque híbrido, ofrecemos la experiencia y adaptabilidad para cubrir tus necesidades.",
    processSteps: [
      { title: "Paso 1: Planificación", text: "Comenzamos con una consulta inicial para definir objetivos, alcance y requisitos. Se desarrolla un plan de cualificación que establece plazos y entregables, garantizando la alineación con las expectativas regulatorias." },
      { title: "Paso 2: Ejecución", text: "Nuestro equipo realiza evaluaciones in situ y pruebas funcionales de los sistemas de utilities. Verificamos el cumplimiento de los estándares de rendimiento mediante inspecciones y mediciones rigurosas." },
      { title: "Paso 3: Documentación", text: "Se crean informes detallados que documentan protocolos de pruebas, resultados y cualquier observación. Todos los registros demuestran el cumplimiento de los estándares regulatorios." },
      { title: "Paso 4: Revisión final y validación", text: "Revisamos los hallazgos, abordamos cualquier brecha y finalizamos el proceso de cualificación. Se proponen acciones correctivas cuando sea necesario para cumplir los estándares requeridos." },
    ],
    partnerTitle: "¿Por qué elegir QbD Group?",
    partnerIntro: "Al elegir QbD Group para la cualificación de utilities, te beneficias de:",
    partnerBullets: [
      "Gestión de proyectos experta: dirección clara y pragmática que asegura una ejecución puntual y conforme.",
      "Eficiencia en costes: reduce los costes globales del proyecto minimizando riesgos y optimizando procesos.",
      "Experiencia regulatoria: asegura el pleno cumplimiento de los estándares GMP para evitar interrupciones.",
      "Excelencia operativa: maximiza la eficiencia y fiabilidad a través de sistemas de utilities validados.",
      "Modelos de soporte flexibles: elige entre soluciones llave en mano o híbridas según tus necesidades específicas.",
    ],
  },

  "equipment-qualification": {
    whatTitle: "¿Qué es la cualificación de equipos?",
    whatParagraphs: [
      "La cualificación de equipos es el proceso de asegurar que la maquinaria, los instrumentos o los sistemas utilizados en producción, ensayos de laboratorio o cualquier otro proceso funcionan de manera consistente según lo previsto.",
      "Incluye actividades documentadas para demostrar que los equipos operan dentro de un conjunto de especificaciones predefinidas y producen resultados fiables. Este proceso es fundamental para garantizar el cumplimiento de los estándares de la industria y mantener la calidad del producto y la consistencia operativa.",
    ],
    whyTitle: "¿Por qué es importante la cualificación de equipos?",
    whyIntro: "La cualificación de equipos es un paso esencial para garantizar el cumplimiento normativo, la eficiencia operativa y la calidad del producto. Estas son las razones:",
    whyBenefits: [
      { title: "Cumplimiento normativo", text: "Garantiza la alineación con los estándares de la industria y los requisitos regulatorios, reduciendo el riesgo de incumplimiento." },
      { title: "Rendimiento fiable", text: "Confirma que los equipos producen resultados precisos y repetibles de forma consistente." },
      { title: "Mitigación de riesgos", text: "Identifica y resuelve posibles problemas antes de iniciar la producción, minimizando tiempos de inactividad y fallos." },
      { title: "Garantía de calidad del producto", text: "Asegura que los procesos cumplen los estándares de calidad, protegiendo la integridad del producto final." },
      { title: "Eficiencia operativa", text: "Optimiza los procesos asegurando que los equipos funcionan a su máximo rendimiento." },
    ],
    processTitle: "Nuestro proceso",
    processIntro: "QbD Group ofrece servicios flexibles y escalables de cualificación de equipos adaptados a los requisitos de tu proyecto. Ya necesites una solución llave en mano, donde QbD gestiona todo el proyecto desde la planificación y ejecución hasta la documentación, o un enfoque híbrido, ofrecemos la experiencia y adaptabilidad para cubrir tus necesidades.",
    processSteps: [
      { title: "Paso 1: Planificación y diseño", text: "Comenzamos con un análisis en profundidad de tus necesidades, desarrollando especificaciones de requisitos de usuario (URS) y definiendo criterios de diseño claros. Se construye una matriz de trazabilidad para hacer seguimiento de los requisitos a lo largo del proceso." },
      { title: "Paso 2: Commissioning y cualificación", text: "Construimos y ejecutamos protocolos de cualificación de instalación (IQ) y cualificación operacional (OQ). Nuestro equipo asegura el cumplimiento de las especificaciones y documenta todos los resultados." },
      { title: "Paso 3: Cualificación de rendimiento", text: "Definimos escenarios de cualificación de rendimiento (PQ), creamos protocolos y ejecutamos pruebas en planta para verificar el rendimiento de los equipos en condiciones reales." },
      { title: "Paso 4: Informe final y validación", text: "Se genera un informe completo que resume los resultados, hallazgos y cualquier acción correctiva necesaria. El proceso concluye con validación documentada para cumplir los estándares regulatorios." },
    ],
    partnerTitle: "¿Por qué elegir QbD Group?",
    partnerIntro: "Al asociarte con QbD Group, te beneficias de:",
    partnerBullets: [
      "Gestión de proyectos experta: dirección clara y pragmática para asegurar una ejecución puntual y conforme.",
      "Eficiencia en costes: reduce los costes globales mediante una gestión de proyectos eficaz y mitigación de riesgos.",
      "Experiencia regulatoria: asegura el cumplimiento de todos los estándares regulatorios, minimizando riesgos y retrasos.",
      "Soluciones escalables: enfoques flexibles para adaptarse al alcance y requisitos de tu proyecto.",
      "Enfoque right-first-time: el pragmatismo y la eficiencia forman parte de nuestro ADN, asegurando que tu proyecto se entrega correctamente a la primera.",
    ],
  },

  "process-validation": {
    whatTitle: "¿Qué es la validación de procesos?",
    whatParagraphs: [
      "<strong class='text-foreground'>La validación de procesos</strong> es la recopilación y evaluación de datos desde el desarrollo hasta la producción comercial. Establece la evidencia científica de que un proceso es capaz de entregar de forma consistente productos de calidad.",
      "Implica una serie de actividades realizadas a lo largo del ciclo de vida del producto y del proceso. La validación de procesos <strong class='text-foreground'>garantiza la seguridad, eficacia y cumplimiento de los medicamentos</strong>, al tiempo que ayuda a reducir los costes de producción y a mejorar la robustez y eficiencia de tus procesos de fabricación. Es una herramienta crítica para identificar riesgos potenciales durante la fabricación y asegurar la calidad para el paciente.",
      "Nuestros expertos pueden configurar, mejorar y dar soporte a la cualificación y validación de tus procesos. El enfoque real se adapta a tus necesidades específicas.",
    ],
    whyTitle: "¿Por qué es importante la validación de procesos?",
    whyIntro: "La validación de procesos es una parte vital para garantizar el cumplimiento y la eficiencia operativa. Estas son las razones:",
    whyBenefits: [
      { title: "Cumplimiento normativo", text: "Demuestra la adhesión a los estándares de la industria y los requisitos regulatorios." },
      { title: "Garantía de calidad del producto", text: "Asegura la consistencia y fiabilidad en la fabricación, protegiendo la seguridad del paciente." },
      { title: "Eficiencia en costes", text: "Reduce los costes de producción identificando riesgos tempranamente y minimizando errores." },
      { title: "Gestión de riesgos", text: "Detecta y mitiga proactivamente posibles fallos antes de que impacten en la producción." },
      { title: "Excelencia operativa", text: "Optimiza los procesos para mejorar la eficiencia y robustez." },
    ],
    processTitle: "Nuestro proceso",
    processIntro: "QbD Group ofrece servicios flexibles y escalables de validación de procesos adaptados a las necesidades de tu proyecto. Ya necesites la dirección completa del proyecto, donde QbD lidera la planificación, ejecución e informes, o soporte colaborativo para complementar tu experiencia interna, ofrecemos el nivel adecuado de asistencia para garantizar el éxito.",
    processSteps: [
      { title: "Paso 1: Planificación y desarrollo de la estrategia", text: "Comenzamos analizando tus necesidades y definiendo la estrategia de validación. Un plan claro establece los requisitos regulatorios, plazos y entregables." },
      { title: "Paso 2: Desarrollo de protocolos", text: "Nuestro equipo diseña protocolos de validación detallados, incluyendo cualificación de instalación (IQ), cualificación operacional (OQ) y cualificación de rendimiento (PQ)." },
      { title: "Paso 3: Ejecución y pruebas", text: "Realizamos pruebas de procesos y recopilación de datos para verificar la consistencia, el cumplimiento y el rendimiento en condiciones reales." },
      { title: "Paso 4: Documentación e informes", text: "Se generan informes completos que resumen los resultados, observaciones y cualquier acción correctiva necesaria. Toda la documentación respalda las presentaciones regulatorias y auditorías." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "Al elegir QbD Group para la validación de procesos, te beneficias de:",
    partnerBullets: [
      "Gestión de proyectos experta: dirección clara y pragmática que asegura una ejecución puntual y conforme.",
      "Experiencia regulatoria: cumplimiento de las últimas normativas en ATMP, biotecnología, dispositivos médicos, moléculas pequeñas y genéricos.",
      "Eficiencia en costes: optimiza recursos minimizando riesgos y mejorando la robustez de los procesos.",
      "Enfoque right-first-time: entrega del proyecto en plazo y con resultados de alta calidad.",
      "Soluciones a medida: soporte flexible adaptado a tus necesidades y procesos específicos.",
    ],
  },

  "cleaning-validation": {
    whatTitle: "¿Qué es la validación de limpieza?",
    whatParagraphs: [
      "La validación de limpieza es el proceso de confirmar que los procedimientos de limpieza eliminan de forma eficaz y reproducible los residuos y contaminantes de las superficies de los equipos utilizados en los procesos de fabricación. Implica pruebas sistemáticas y documentación para asegurar que los métodos de limpieza cumplen consistentemente los estándares y requisitos regulatorios predeterminados.",
      "La validación de limpieza no solo contribuye a la seguridad y eficacia de los medicamentos, sino que también garantiza el cumplimiento normativo, reduce los costes de producción y mejora la robustez y eficiencia de los procesos de fabricación. Ayuda a determinar los escenarios de peor caso e identificar riesgos que pueden surgir durante la producción, protegiendo la calidad del producto.",
      "Nuestros expertos pueden configurar, mejorar y dar soporte a la cualificación y validación de tus procesos de limpieza, facilitando operaciones seguras y conformes.",
    ],
    whyTitle: "¿Por qué es importante la validación de limpieza?",
    whyIntro: "La validación de limpieza es un aspecto crítico para garantizar el cumplimiento, la eficiencia operativa y la seguridad del producto. Estas son las razones:",
    whyBenefits: [
      { title: "Cumplimiento normativo", text: "Demuestra la adhesión a los estándares GMP y las expectativas regulatorias." },
      { title: "Seguridad del producto", text: "Asegura la eliminación de residuos para prevenir la contaminación cruzada y mantener la integridad del producto." },
      { title: "Eficiencia en costes", text: "Reduce los tiempos de inactividad de producción y mitiga los riesgos de rechazo de lotes o retiradas del mercado." },
      { title: "Consistencia operativa", text: "Mejora la fiabilidad y reproducibilidad de los procesos, asegurando un rendimiento consistente." },
      { title: "Gestión de riesgos", text: "Identifica y minimiza los riesgos asociados a la contaminación, protegiendo la seguridad del paciente." },
    ],
    processTitle: "Nuestro proceso",
    processIntro: "QbD Group ofrece servicios de validación de limpieza diseñados a medida para cumplir los requisitos específicos de tu proyecto. Ya necesites la dirección completa del proyecto, con QbD gestionando la planificación, ejecución e informes, o soporte colaborativo para reforzar tus equipos existentes, ofrecemos soluciones flexibles para garantizar tu éxito.",
    processSteps: [
      { title: "Paso 1: Planificación y evaluación de riesgos", text: "Comenzamos con un análisis en profundidad de los requisitos y riesgos de limpieza. Un plan de validación establece los objetivos, plazos y entregables para asegurar el cumplimiento y la preparación." },
      { title: "Paso 2: Desarrollo de protocolos", text: "Nuestros expertos desarrollan protocolos detallados, incluyendo técnicas de muestreo, criterios de aceptación y métodos de prueba, para validar la eficacia de la limpieza." },
      { title: "Paso 3: Pruebas y ejecución", text: "Ejecutamos pruebas en condiciones reales, recopilando y analizando datos para verificar el cumplimiento y la eficiencia de la limpieza." },
      { title: "Paso 4: Informes y mejora", text: "Los informes completos resumen los resultados, observaciones y recomendaciones para la mejora continua. Proporcionamos documentación completa para auditorías y presentaciones regulatorias." },
    ],
    partnerTitle: "¿Por qué asociarte con QbD Group?",
    partnerIntro: "Al elegir QbD Group para la validación de limpieza, te beneficias de:",
    partnerBullets: [
      "Gestión de proyectos experta: dirección clara y pragmática que asegura una ejecución puntual y conforme.",
      "Metodologías avanzadas: enfoques probados para cumplir estrictos estándares de eficiencia y regulatorios.",
      "Experiencia regulatoria: cumplimiento de las últimas normativas GMP y las mejores prácticas de la industria.",
      "Eficiencia en costes: optimiza recursos minimizando riesgos y mejorando la fiabilidad de los procesos.",
      "Soluciones personalizadas: soporte flexible adaptado a tus necesidades y procesos específicos.",
    ],
  },

  "analytical-method-validation": {
    whatTitle: "¿Qué es la validación de métodos analíticos?",
    whatParagraphs: [
      "La validación de métodos analíticos es el proceso de confirmar que los métodos analíticos utilizados para ensayar sustancias, productos o materiales son adecuados para su finalidad prevista y producen consistentemente resultados fiables.",
      "Evalúa las características de rendimiento del método, como exactitud, precisión, especificidad y robustez, para asegurar su idoneidad para el uso. Nuestros expertos ofrecen soluciones a medida para validar tus métodos analíticos, garantizando el cumplimiento normativo y resultados de alta calidad.",
    ],
    whyTitle: "¿Por qué es importante la validación de métodos analíticos?",
    whyIntro: "La validación de métodos analíticos es esencial para mantener la calidad, el cumplimiento y la excelencia operativa. Estas son las razones:",
    whyBenefits: [
      { title: "Exactitud de los datos", text: "Asegura que los métodos analíticos generan datos precisos y exactos, proporcionando información fiable para la toma de decisiones en investigación, desarrollo y control de calidad." },
      { title: "Cumplimiento normativo", text: "Cumple los requisitos estrictos de las agencias regulatorias, asegurando que los resultados de los ensayos cumplen con los estándares y directrices establecidos." },
      { title: "Garantía de calidad", text: "Contribuye a la calidad del producto confirmando que los métodos cumplen las especificaciones, mitigando el riesgo de incumplimiento, retiradas y problemas de seguridad." },
      { title: "Optimización de procesos", text: "Proporciona información sobre el rendimiento del método, permitiendo la optimización y mejoras para aumentar la eficiencia y fiabilidad a lo largo del tiempo." },
    ],
    processTitle: "Nuestro proceso",
    processIntro: "QbD Group ofrece servicios flexibles y escalables de validación de métodos analíticos adaptados a los requisitos de tu proyecto. Ya necesites una solución llave en mano, con QbD supervisando la planificación, ejecución y documentación, o un enfoque híbrido donde tu equipo gestiona el proyecto mientras QbD se encarga de las actividades de validación, proporcionamos el soporte que necesitas para el éxito.",
    processSteps: [
      { title: "Paso 1: Planificación y evaluación", text: "Comenzamos con una consulta inicial para definir objetivos, requisitos regulatorios y criterios de rendimiento. Un plan de validación detallado establece plazos y entregables." },
      { title: "Paso 2: Desarrollo de protocolos", text: "Nuestro equipo desarrolla protocolos de validación, incluyendo métodos de ensayo y criterios de aceptación para exactitud, precisión, especificidad y robustez." },
      { title: "Paso 3: Pruebas y ejecución", text: "Ejecutamos estudios de validación, recopilando datos en condiciones predefinidas para evaluar el rendimiento y fiabilidad del método." },
      { title: "Paso 4: Informes y revisión de cumplimiento", text: "Los informes completos resumen hallazgos, resultados y cualquier acción correctiva. Se prepara toda la documentación para respaldar presentaciones regulatorias y auditorías." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Al elegir QbD Group para la validación de métodos analíticos, te beneficias de:",
    partnerBullets: [
      "Gestión de proyectos experta: dirección clara y pragmática que asegura una ejecución puntual y conforme.",
      "Experiencia regulatoria: cumplimiento de los últimos estándares y directrices de la industria para ensayos analíticos.",
      "Eficiencia en costes: minimiza riesgos y optimiza recursos mejorando la exactitud y fiabilidad de los datos.",
      "Soluciones a medida: enfoques flexibles adaptados a tus necesidades y procesos específicos.",
      "Confianza en los resultados: métodos validados que respaldan la toma de decisiones informada y la calidad del producto.",
    ],
  },

  "sterilization-validation": {
    whatTitle: "¿Qué es la validación de esterilización?",
    whatParagraphs: [
      "La validación de esterilización es el proceso de confirmar que los métodos de esterilización eliminan eficazmente los microorganismos de productos, equipos o superficies para asegurar que cumplen los niveles de aseguramiento de esterilidad predeterminados.",
      "Implica pruebas y documentación para demostrar que los procesos de esterilización logran consistentemente el nivel deseado de reducción microbiana. Este proceso de validación es vital para industrias como la farmacéutica, los dispositivos médicos y la producción alimentaria, donde la esterilidad es esencial para la calidad del producto y la seguridad del consumidor.",
    ],
    whyTitle: "¿Por qué es esencial la validación de esterilización?",
    whyIntro: "La validación de esterilización es crucial para mantener la seguridad, el cumplimiento y la excelencia operativa. Estas son las razones:",
    whyBenefits: [
      { title: "Prevención de contaminación", text: "Asegura que los productos están libres de microorganismos dañinos, reduciendo los riesgos de contaminación y protegiendo la salud del consumidor." },
      { title: "Cumplimiento normativo", text: "Cumple los estrictos estándares de seguridad y calidad exigidos por los organismos reguladores, especialmente en los sectores sanitario, farmacéutico y de producción alimentaria." },
      { title: "Mitigación de riesgos", text: "Identifica y mitiga los riesgos asociados a la contaminación microbiana, previniendo infecciones e incidentes de seguridad." },
      { title: "Garantía de calidad", text: "Respalda la producción consistente de productos estériles, asegurando la satisfacción del cliente y la aprobación regulatoria." },
    ],
    processTitle: "Nuestro proceso",
    processIntro: "QbD Group ofrece servicios flexibles y escalables de validación de esterilización adaptados a los requisitos de tu proyecto. Ya necesites una solución llave en mano, con QbD gestionando la planificación, ejecución y documentación, o un enfoque híbrido donde tu equipo supervisa la gestión del proyecto mientras QbD se encarga de las actividades de validación, ofrecemos el soporte que necesitas para el éxito.",
    processSteps: [
      { title: "Paso 1: Planificación y evaluación de riesgos", text: "Comenzamos con una consulta inicial para definir objetivos, requisitos regulatorios y criterios de rendimiento. Un plan de validación detallado establece plazos y entregables." },
      { title: "Paso 2: Desarrollo de protocolos", text: "Nuestros expertos desarrollan protocolos de validación, incluyendo ciclos de esterilización, métodos de prueba y criterios de aceptación, asegurando el cumplimiento de los estándares regulatorios." },
      { title: "Paso 3: Ejecución y pruebas", text: "Ejecutamos estudios de validación de esterilización, realizando pruebas biológicas y químicas para verificar los niveles de aseguramiento de esterilidad y el cumplimiento." },
      { title: "Paso 4: Informes y revisión de cumplimiento", text: "Los informes completos resumen los resultados, observaciones y acciones correctivas cuando sea necesario. Toda la documentación respalda las presentaciones regulatorias y auditorías." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Al elegir QbD Group para la validación de esterilización, te beneficias de:",
    partnerBullets: [
      "Gestión de proyectos experta: dirección clara y pragmática que asegura una ejecución puntual y conforme.",
      "Experiencia regulatoria: cumplimiento de los últimos estándares y directrices de aseguramiento de esterilidad.",
      "Eficiencia en costes: optimiza recursos minimizando riesgos y mejorando la fiabilidad de los procesos.",
      "Soluciones a medida: enfoques flexibles adaptados a tus necesidades y procesos específicos.",
      "Enfoque right-first-time: pragmatismo y precisión para entregar resultados que cumplen los más altos estándares.",
    ],
  },

  "tech-transfer": {
    whatTitle: "¿Qué es la transferencia tecnológica?",
    whatParagraphs: [
      "Lanzar un nuevo producto o fármaco al mercado, externalizar tu producción o trasladarla requiere tiempo y esfuerzo. Cualquier retraso puede suponer una pérdida de ingresos. Una transferencia eficiente de tecnologías entre todas las partes implicadas puede acelerar estos procesos.",
      "La transferencia tecnológica se refiere al proceso de transferir conocimientos, tecnologías o procesos de una organización a otra entidad. Implica transferir no solo los aspectos técnicos, sino también la propiedad intelectual asociada, la documentación y la experiencia necesaria para utilizar y replicar eficazmente la tecnología o proceso.",
      "QbD Group te ayuda a transferir tecnologías con éxito en muchas circunstancias diferentes:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong class='text-foreground'>Fusiones y adquisiciones:</strong> durante fusiones o adquisiciones, la transferencia de información y tecnologías entre empresas es crucial.</li><li><strong class='text-foreground'>Externalización:</strong> transferir experiencia tecnológica, procesos y a veces incluso empleados es esencial en la externalización.</li><li><strong class='text-foreground'>Spin-offs:</strong> los spin-offs requieren un enfoque en la investigación y desarrollo y la transferencia tecnológica para garantizar el éxito.</li></ul>",
    ],
    whyTitle: "Objetivo",
    whyIntro: "La transferencia tecnológica es crucial para la innovación, la comercialización y el crecimiento económico. Estas son las razones:",
    whyBenefits: [
      { title: "Adopción de la innovación", text: "Facilita la adopción y utilización de nuevas tecnologías o procesos, permitiendo su transición desde el desarrollo hasta aplicaciones del mundo real." },
      { title: "Comercialización", text: "Desempeña un papel clave en llevar nuevos productos, procesos o tecnologías al mercado, asegurando un escalado fluido y la preparación para la producción." },
      { title: "Colaboración y alianzas", text: "Fomenta las alianzas y el intercambio de conocimientos entre organizaciones, impulsando la innovación." },
      { title: "Crecimiento económico", text: "Impulsa la innovación, apoya el desarrollo empresarial y crea nuevas oportunidades de mercado, potenciando la competitividad." },
    ],
    processTitle: "Nuestro proceso",
    processIntro: "QbD Group ofrece servicios de transferencia tecnológica a medida para simplificar y acelerar tu transición. Ya necesites una solución llave en mano, con QbD gestionando la planificación, ejecución y documentación, o un enfoque híbrido donde tu equipo supervisa la gestión del proyecto mientras QbD se encarga de las actividades de transferencia técnica, ofrecemos la experiencia y el soporte para garantizar un proceso fluido.",
    processSteps: [
      { title: "Fase 1: Funciones y responsabilidades", text: "Comenzamos con la preparación del proyecto, abordando los requisitos legales y organizativos. Se definen claramente los roles y responsabilidades para sentar las bases del éxito." },
      { title: "Fase 2: Intercambio de datos", text: "Todas las partes determinan qué información se intercambiará. QbD proporciona un checklist de transferencia tecnológica, plantillas y asesoramiento a medida para garantizar un intercambio de datos fluido." },
      { title: "Fase 3: Pruebas y validación de procesos", text: "El receptor analiza el proceso del emisor, lo alinea con los procedimientos internos y equipos, y realiza la validación. Los expertos en validación de QbD supervisan las pruebas y la cualificación del proceso." },
      { title: "Fase 4: Dossier de registro y lecciones aprendidas", text: "Toda la información se compila en el dossier de registro, con el checklist de QbD cubriendo los puntos clave. Se revisa el cumplimiento normativo y se recogen las lecciones aprendidas para mejorar futuros procesos." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Al asociarte con QbD Group para la transferencia tecnológica, te beneficias de:",
    partnerBullets: [
      "Gestión de proyectos experta: dirección clara y pragmática que asegura una ejecución fluida y conforme.",
      "Eficiencia en costes: minimiza retrasos y reduce costes con procesos estructurados y metodologías probadas.",
      "Experiencia regulatoria: asegura el cumplimiento de los requisitos legales y evita escollos durante las transiciones.",
      "Soluciones personalizadas: enfoques flexibles para adaptarse a tus necesidades, ya sea para fusiones, externalización o spin-offs.",
      "Enfoque right-first-time: eficiencia, precisión y optimización en el núcleo de nuestra prestación de servicios.",
    ],
  },

  "process-product-support": {
    whatTitle: "¿Qué es el soporte de procesos y productos?",
    whatParagraphs: [
      "<strong class='text-foreground'>El soporte de procesos y productos implica proporcionar asistencia, orientación y recursos para optimizar los procesos de fabricación y mejorar la calidad de los productos.</strong>",
      "En lo que respecta a los procesos, el soporte implica optimizar los flujos de trabajo, identificar y eliminar ineficiencias, implementar las mejores prácticas y asegurar el cumplimiento de los estándares regulatorios. Esto incluye la realización de auditorías de procesos, la formación del personal y la implementación de sistemas de gestión de la calidad para mantener la consistencia y fiabilidad en la producción.",
      "Para los productos, el soporte implica asegurar que los productos cumplen los estándares de calidad, los requisitos regulatorios y las expectativas de los clientes. Esto incluye la realización de ensayos y validación de productos, la resolución de problemas, la implementación de mejoras basadas en feedback y la gestión de la documentación del producto a lo largo de su ciclo de vida.",
      "En conjunto, el soporte de procesos y productos tiene como objetivo <strong class='text-foreground'>impulsar la eficiencia operativa, mejorar la calidad del producto y, en última instancia, mejorar la satisfacción del cliente</strong>. Implica una combinación de experiencia en optimización de procesos, gestión de la calidad, cumplimiento normativo y metodologías de mejora continua para asegurar que tanto los procesos como los productos cumplen los más altos estándares de excelencia.",
    ],
    whyTitle: "¿Por qué es importante el soporte de procesos y productos?",
    whyIntro: "El soporte de procesos y productos es esencial para garantizar la eficiencia, el cumplimiento y la calidad. Estas son las razones:",
    whyBenefits: [
      { title: "Eficiencia operativa", text: "Optimiza los procesos de fabricación, identifica ineficiencias e implementa mejoras para maximizar la productividad y reducir costes." },
      { title: "Garantía de calidad", text: "Asegura que los productos cumplen los estándares de calidad, los requisitos regulatorios y las expectativas de los clientes mediante soporte y orientación expertos." },
      { title: "Mejora continua", text: "Fomenta una cultura de mejora continua alentando el feedback, implementando las mejores prácticas y abordando proactivamente los problemas para mejorar la eficiencia y calidad a lo largo del tiempo." },
      { title: "Satisfacción del cliente", text: "Entregar consistentemente productos de alta calidad mejora la satisfacción y fidelidad del cliente, aumentando la competitividad y el crecimiento empresarial." },
    ],
    processTitle: "Nuestro proceso",
    processIntro: "QbD Group ofrece servicios flexibles y escalables de soporte de procesos y productos adaptados a tus necesidades. Ya necesites una solución llave en mano, con QbD gestionando la planificación, ejecución y documentación, o un enfoque híbrido donde tu equipo supervisa la gestión del proyecto mientras QbD se encarga de las actividades de soporte, ofrecemos la experiencia y asistencia para impulsar tu proyecto.",
    processSteps: [
      { title: "Paso 1: Evaluación de necesidades y planificación", text: "Comenzamos analizando tus procesos y productos, identificando oportunidades de mejora y definiendo los objetivos del proyecto. Un plan a medida establece plazos, entregables y requisitos de cumplimiento." },
      { title: "Paso 2: Implementación y optimización", text: "Ejecutamos auditorías de procesos, identificamos brechas e implementamos mejoras. Nuestro equipo optimiza los flujos de trabajo, integra las mejores prácticas y asegura el cumplimiento normativo." },
      { title: "Paso 3: Pruebas y validación", text: "Realizamos ensayos de productos, resolvemos problemas y validamos procesos para asegurar la fiabilidad y consistencia. Todos los hallazgos se documentan para demostrar el cumplimiento." },
      { title: "Paso 4: Mejora continua e informes", text: "Establecemos un marco de seguimiento continuo, recogida de feedback y mejoras continuas para mantener la eficiencia y la calidad del producto a lo largo del tiempo." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Al elegir QbD Group para el soporte de procesos y productos, te beneficias de:",
    partnerBullets: [
      "Gestión de proyectos experta: dirección clara y pragmática que asegura una ejecución fluida y conforme.",
      "Eficiencia en costes: minimiza costes optimizando procesos y mejorando flujos de trabajo.",
      "Experiencia regulatoria: cumplimiento de los estándares y directrices de la industria.",
      "Soluciones a medida: enfoques flexibles adaptados a tus necesidades y retos específicos.",
      "Enfoque right-first-time: eficiencia, precisión y optimización para entregar resultados fiables.",
    ],
  },
};

const offeringContentZH: Record<string, OfferingSectionTextOverride> = {
  "analytical-method-validation": {
    whatTitle: "什么是分析方法验证？",
    whatParagraphs: [
      "分析方法验证是确认用于检测物质、产品或材料的分析方法适用于其预期用途，并能持续产生可靠结果的过程。",
      "它评估方法的性能特征，例如准确度、精密度、专属性和耐用性，以确保其适用性。我们的专家提供量身定制的解决方案来验证您的分析方法，确保法规合规性和高质量的结果。",
    ],
    whyTitle: "为什么分析方法验证很重要？",
    whyIntro: "分析方法验证对于保持质量、合规性和卓越运营至关重要。原因如下：",
    whyBenefits: [
      { title: "数据准确性", text: "确保分析方法生成准确而精确的数据，为研发和质量控制中的决策提供可靠的信息。" },
      { title: "法规合规", text: "满足监管机构的严格要求，确保检测结果符合既定的标准和指南。" },
      { title: "质量保证", text: "通过确认方法符合规范，为产品质量做出贡献，降低不合规、召回和安全问题的风险。" },
      { title: "流程优化", text: "提供对方法性能的洞察，支持优化和改进，从而长期提升效率与可靠性。" },
    ],
    processTitle: "我们的流程",
    processIntro: "QbD Group 提供灵活且可扩展的分析方法验证服务，根据您的项目需求量身定制。无论您需要由 QbD 全程负责规划、执行和文档的交钥匙解决方案，还是您的团队负责项目管理而由 QbD 承担验证活动的混合模式，我们都将提供您成功所需的支持。",
    processSteps: [
      { title: "第 1 步：规划与评估", text: "我们从初步咨询开始，明确目标、法规要求和性能标准。详细的验证计划明确时间表和交付物。" },
      { title: "第 2 步：方案开发", text: "我们的团队开发验证方案，包括测试方法以及关于准确度、精密度、专属性和耐用性的接受标准。" },
      { title: "第 3 步：测试与执行", text: "我们执行验证研究，在预定条件下收集数据，以评估方法的性能和可靠性。" },
      { title: "第 4 步：报告与合规审查", text: "全面的报告总结调查结果、结论以及任何必要的纠正措施。准备完整的文档以支持法规提交和审计。" },
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "当您选择 QbD Group 进行分析方法验证时，您将获得：",
    partnerBullets: [
      "专业项目管理：清晰务实的指导，确保按时执行和合规。",
      "法规专长：符合最新的行业标准和分析检测指南。",
      "成本效益：降低风险并优化资源，同时提高数据的准确性和可靠性。",
      "量身定制的解决方案：灵活的方法以适应您的具体需求和流程。",
      "对结果的信心：经过验证的方法支持明智的决策和产品质量。",
    ],
  },
  "cleaning-validation": {
    whatTitle: "什么是清洁验证？",
    whatParagraphs: [
      "清洁验证是确认清洁程序能够有效且可重现地去除生产过程中所用设备表面上的残留物和污染物的过程。它涉及系统化的测试和文档记录，以确保清洁方法始终满足预定的标准和法规要求。",
      "清洁验证不仅有助于药品的安全性和有效性，还能确保符合法规、降低生产成本，并提升生产工艺的稳健性与效率。它有助于确定最坏情况，并识别生产过程中可能出现的风险，从而保障产品质量。",
      "我们的专家可以为您建立、改进并支持清洁工艺的确认与验证，帮助您实现安全合规的运营。",
    ],
    whyTitle: "为什么清洁验证很重要？",
    whyIntro: "清洁验证是确保合规性、运营效率和产品安全的关键环节。原因如下：",
    whyBenefits: [
      { title: "法规合规", text: "证明遵循 GMP 标准和法规要求。" },
      { title: "产品安全", text: "确保去除残留物，防止交叉污染并维护产品完整性。" },
      { title: "成本效益", text: "减少生产停机时间，降低批次拒收或召回的风险。" },
      { title: "运营一致性", text: "提升工艺的可靠性和可重现性，确保稳定的性能。" },
      { title: "风险管理", text: "识别并最大限度降低与污染相关的风险，保障患者安全。" },
    ],
    processTitle: "我们的流程",
    processIntro: "QbD Group 提供量身定制的清洁验证服务，满足您具体的项目需求。无论您需要由 QbD 全程负责规划、执行和报告的项目管理模式，还是协作式支持以增强您现有团队的能力，我们都将提供灵活的解决方案，助您取得成功。",
    processSteps: [
      { title: "第 1 步：规划与风险评估", text: "我们首先对清洁要求和风险进行深入分析。验证计划明确目标、时间表和交付物，以确保合规性和准备就绪。" },
      { title: "第 2 步：方案开发", text: "我们的专家开发详细的方案，包括取样技术、接受标准和测试方法，以验证清洁的有效性。" },
      { title: "第 3 步：测试与执行", text: "我们在真实条件下执行测试，收集并分析数据以验证清洁的合规性和效率。" },
      { title: "第 4 步：报告与改进", text: "全面的报告总结结果、观察以及持续改进的建议。我们为审计和法规提交提供完整的文档。" },
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "当您选择 QbD Group 进行清洁验证时，您将获得：",
    partnerBullets: [
      "专业项目管理：清晰务实的指导，确保按时执行和合规。",
      "先进方法论：采用经过验证的方法，满足严格的效率和法规标准。",
      "法规专长：符合最新的 GMP 法规和行业最佳实践。",
      "成本效益：在最大限度降低风险的同时优化资源，提升工艺的可靠性。",
      "量身定制的解决方案：灵活的支持，适应您的具体需求和流程。",
    ],
  },
  "equipment-qualification": {
    whatTitle: "什么是设备确认？",
    whatParagraphs: [
      "设备确认是确保用于生产、实验室检测或任何其他流程中的机器、仪器或系统能够持续按预期运行的过程。",
      "它包括书面记录的活动，以证明设备在一组预先定义的规范范围内运行并提供可靠的结果。这一过程对于确保符合行业标准以及维持产品质量和运营一致性至关重要。",
    ],
    whyTitle: "为什么设备确认很重要？",
    whyIntro: "设备确认是确保法规合规、运营效率和产品质量的关键步骤。原因如下：",
    whyBenefits: [
      { title: "法规合规", text: "确保符合行业标准和法规要求，降低不合规的风险。" },
      { title: "可靠的性能", text: "确认设备能够持续提供准确且可重复的结果。" },
      { title: "风险缓解", text: "在生产开始前识别并解决潜在问题，最大限度地减少停机和故障。" },
      { title: "产品质量保证", text: "确保流程符合质量标准，保护最终产品的完整性。" },
      { title: "运营效率", text: "通过确保设备以最佳状态运行来优化流程。" },
    ],
    processTitle: "我们的流程",
    processIntro: "QbD Group 提供灵活且可扩展的设备确认服务，根据您的项目需求量身定制。无论您需要由 QbD 全程负责从规划、执行到文档的交钥匙解决方案，还是混合模式，我们都将提供专业能力与适应性来满足您的需求。",
    processSteps: [
      { title: "第 1 步：规划与设计", text: "我们首先深入分析您的需求，制定用户需求规范 (URS) 并定义清晰的设计标准。建立可追溯性矩阵以在整个流程中追踪需求。" },
      { title: "第 2 步：试运行与确认", text: "我们建立并执行安装确认 (IQ) 与运行确认 (OQ) 方案。我们的团队确保符合规范并记录所有结果。" },
      { title: "第 3 步：性能确认", text: "我们定义性能确认 (PQ) 场景，制定方案，并在车间执行测试，以验证设备在真实条件下的性能。" },
      { title: "第 4 步：最终报告与验证", text: "生成全面的报告，总结结果、发现以及任何必要的纠正措施。流程以书面验证作为结尾，以满足法规标准。" },
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "当您与 QbD Group 合作时，您将获得：",
    partnerBullets: [
      "专业项目管理：清晰务实的指导，确保按时执行和合规。",
      "成本效益：通过高效的项目管理与风险缓解降低整体成本。",
      "法规专长：确保符合所有法规标准，最大限度地减少风险与延误。",
      "可扩展的解决方案：灵活的方法以匹配您的项目范围与需求。",
      "一次做对的方法：务实与高效是我们的 DNA，确保您的项目首次即正确交付。",
    ],
  },
  "process-product-support": {
    whatTitle: "什么是工艺与产品支持？",
    whatParagraphs: [
      "<strong class='text-foreground'>工艺与产品支持是指提供协助、指导和资源，以优化生产工艺并提升产品质量。</strong>",
      "在工艺层面，支持工作包括优化工作流程、识别并消除低效环节、落实最佳实践，以及确保符合法规标准。这意味着开展工艺审计、为人员提供培训，并部署质量管理体系，以保持生产的稳定性与可靠性。",
      "在产品层面，支持工作意味着确保产品满足质量标准、法规要求及客户期望。这包括开展产品测试与验证、问题排查、基于反馈实施产品改进，以及在整个生命周期内管理产品文档。",
      "总体而言，工艺与产品支持旨在<strong class='text-foreground'>提升运营效率、提高产品质量，并最终改善客户满意度</strong>。它结合了工艺优化、质量管理、法规合规以及持续改进方法等方面的专业能力，确保工艺与产品都达到最高的卓越标准。",
    ],
    whyTitle: "为什么工艺与产品支持很重要？",
    whyIntro: "工艺与产品支持是确保效率、合规性与质量的关键环节。原因如下：",
    whyBenefits: [
      { title: "运营效率", text: "优化生产工艺，识别低效环节并实施改进，以最大化生产力并降低成本。" },
      { title: "质量保证", text: "通过专业的支持与指导，确保产品满足质量标准、法规要求和客户期望。" },
      { title: "持续改进", text: "通过鼓励反馈、落实最佳实践并主动解决问题，营造持续改进的文化，从而不断提升效率与质量。" },
      { title: "客户满意度", text: "始终如一地交付高质量产品可提升客户满意度与忠诚度，增强竞争力并推动业务增长。" },
    ],
    processTitle: "我们的流程",
    processIntro: "QbD Group 提供灵活且可扩展的工艺与产品支持服务，根据您的需求量身定制。无论您需要由 QbD 负责规划、执行与文档的交钥匙解决方案，还是由您的团队负责项目管理而由 QbD 承担支持活动的混合模式，我们都将提供推动项目前进所需的专业能力与协助。",
    processSteps: [
      { title: "第 1 步：需求评估与规划", text: "我们首先分析您的工艺与产品，识别改进机会并明确项目目标。量身定制的方案将明确时间表、交付物及合规要求。" },
      { title: "第 2 步：实施与优化", text: "我们开展工艺审计，识别差距并实施改进。我们的团队优化工作流程、整合最佳实践，并确保符合法规要求。" },
      { title: "第 3 步：测试与验证", text: "我们进行产品测试、问题排查并验证工艺，以确保可靠性与稳定性。所有结果均会记录在案，以证明合规性。" },
      { title: "第 4 步：持续改进与报告", text: "我们建立持续监控、反馈收集与持续改进的框架，以长期维持效率与产品质量。" },
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "当您选择 QbD Group 进行工艺与产品支持时，您将获得：",
    partnerBullets: [
      "专业项目管理：清晰务实的指导，确保顺畅执行与合规。",
      "成本效益：通过优化工艺与改进工作流程来降低成本。",
      "法规专长：符合行业标准与指南。",
      "量身定制的解决方案：灵活的方法，以契合您的具体需求与挑战。",
      "一次做对的方法：以效率、精确与优化为核心，交付可靠的结果。",
    ],
  },
  "process-validation": {
    whatTitle: "什么是工艺验证？",
    whatParagraphs: [
      "<strong class='text-foreground'>工艺验证</strong>是指从研发到商业化生产过程中数据的收集与评估。它建立起科学证据，证明某一工艺能够持续稳定地交付符合质量要求的产品。",
      "工艺验证涉及在产品与工艺生命周期内开展的一系列活动。它能够<strong class='text-foreground'>保障药品的安全性、有效性与合规性</strong>，同时有助于降低生产成本，提升生产工艺的稳健性与效率。它是识别生产过程中潜在风险并为患者把控质量的关键工具。",
      "我们的专家可以为您建立、改进并支持工艺的确认与验证，方案完全根据您的具体需求量身定制。",
    ],
    whyTitle: "为什么工艺验证很重要？",
    whyIntro: "工艺验证是确保合规性与运营效率的关键环节。原因如下：",
    whyBenefits: [
      { title: "法规合规", text: "证明对行业标准与法规要求的遵循。" },
      { title: "产品质量保证", text: "确保生产的一致性与可靠性，保障患者安全。" },
      { title: "成本效益", text: "通过尽早识别风险并减少错误来降低生产成本。" },
      { title: "风险管理", text: "在影响生产之前主动识别并缓解潜在故障。" },
      { title: "卓越运营", text: "优化工艺以提升效率与稳健性。" },
    ],
    processTitle: "我们的流程",
    processIntro: "QbD Group 提供灵活且可扩展的工艺验证服务，根据您的项目需求量身定制。无论您需要由 QbD 全程主导规划、执行与报告的完整项目管理，还是希望以协作式支持补充您的内部能力，我们都将提供适当的支持水平，确保您的成功。",
    processSteps: [
      { title: "第 1 步：规划与策略制定", text: "我们首先分析您的需求并明确验证策略。清晰的方案将明确法规要求、时间表与交付物。" },
      { title: "第 2 步：方案开发", text: "我们的团队设计详细的验证方案，包括安装确认 (IQ)、运行确认 (OQ) 与性能确认 (PQ)。" },
      { title: "第 3 步：执行与测试", text: "我们开展工艺测试与数据收集，在真实条件下验证一致性、合规性与性能。" },
      { title: "第 4 步：文档与报告", text: "生成完整的报告，总结结果、观察以及任何必要的纠正措施。所有文档均可支持法规提交与审计。" },
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "当您选择 QbD Group 进行工艺验证时，您将获得：",
    partnerBullets: [
      "专业项目管理：清晰务实的指导，确保按时执行与合规。",
      "法规专长：符合 ATMP、生物技术、医疗器械、小分子与仿制药领域的最新法规。",
      "成本效益：在降低风险并提升工艺稳健性的同时优化资源。",
      "一次做对的方法：按期交付项目，并取得高质量成果。",
      "量身定制的解决方案：灵活的支持，契合您的具体需求与工艺。",
    ],
  },
  "sterilization-validation": {
    whatTitle: "什么是灭菌验证？",
    whatParagraphs: [
      "灭菌验证是确认灭菌方法能够有效消除产品、设备或表面上的微生物，确保达到预定灭菌保证水平的过程。",
      "它通过测试和文档记录来证明灭菌工艺能够持续达到所需的微生物减少水平。该验证过程对制药、医疗器械及食品生产等行业至关重要，在这些行业中，无菌是产品质量与消费者安全的基础。",
    ],
    whyTitle: "为什么灭菌验证至关重要？",
    whyIntro: "灭菌验证对于维护安全、合规与卓越运营至关重要。原因如下：",
    whyBenefits: [
      { title: "防止污染", text: "确保产品不含有害微生物，降低污染风险并保护消费者健康。" },
      { title: "法规合规", text: "满足监管机构严格的安全与质量标准，尤其适用于医疗、制药及食品生产行业。" },
      { title: "风险缓解", text: "识别并缓解与微生物污染相关的风险，防止感染和安全事件的发生。" },
      { title: "质量保证", text: "支持稳定生产无菌产品，确保客户满意度并获得法规批准。" },
    ],
    processTitle: "我们的流程",
    processIntro: "QbD Group 提供灵活且可扩展的灭菌验证服务，根据您的项目需求量身定制。无论您需要由 QbD 全程主导规划、执行与文档的交钥匙解决方案，还是由您的团队负责项目管理而由 QbD 承担验证活动的混合模式，我们都将提供您成功所需的支持。",
    processSteps: [
      { title: "第 1 步：规划与风险评估", text: "我们首先开展初步咨询，明确目标、法规要求与性能标准。详细的验证计划将明确时间表与交付物。" },
      { title: "第 2 步：方案开发", text: "我们的专家开发验证方案，包括灭菌循环、测试方法及接受标准，确保符合法规标准。" },
      { title: "第 3 步：执行与测试", text: "我们开展灭菌验证研究，进行生物与化学测试，以核验灭菌保证水平与合规性。" },
      { title: "第 4 步：报告与合规审查", text: "全面的报告将总结结果、观察及在必要时采取的纠正措施。所有文档均可支持法规提交与审计。" },
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "当您选择 QbD Group 进行灭菌验证时，您将获得：",
    partnerBullets: [
      "专业项目管理：清晰务实的指导，确保按时执行与合规。",
      "法规专长：符合最新的无菌保证标准与指南。",
      "成本效益：在降低风险并提升工艺可靠性的同时优化资源。",
      "量身定制的解决方案：灵活的方法以契合您的具体需求与流程。",
      "一次做对的方法：以务实与精确交付符合最高标准的成果。",
    ],
  },
  "tech-transfer": {
    whatTitle: "什么是技术转移？",
    whatParagraphs: [
      "将新产品或新药推向市场、外包您的生产或进行生产迁移都需要投入时间和精力。任何延误都可能造成收入损失。在所有相关方之间高效地转移技术，可以加快这些流程。",
      "技术转移是指将知识、技术或工艺从一个组织转移到另一实体的过程。它不仅涉及技术层面的转移，还包括相关的知识产权、文档及有效使用和复制该技术或工艺所需的专业能力。",
      "QbD Group 在多种不同情境下帮助您成功完成技术转移：<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong class='text-foreground'>并购：</strong>在并购过程中，企业之间的信息与技术转移至关重要。</li><li><strong class='text-foreground'>外包：</strong>在外包中，转移技术经验、工艺有时甚至员工都是必不可少的。</li><li><strong class='text-foreground'>分拆：</strong>分拆公司需要专注于研发与技术转移，以确保获得成功。</li></ul>",
    ],
    whyTitle: "目标",
    whyIntro: "技术转移对创新、商业化和经济增长至关重要。原因如下：",
    whyBenefits: [
      { title: "采用创新", text: "促进新技术或新工艺的采用与应用，使其能够从研发顺利过渡到实际应用。" },
      { title: "商业化", text: "在将新产品、工艺或技术推向市场的过程中发挥关键作用，确保平稳放大并具备生产准备。" },
      { title: "协作与合作伙伴关系", text: "促进组织之间的合作与知识共享，推动创新发展。" },
      { title: "经济增长", text: "推动创新、支持企业发展并创造新的市场机会，从而增强竞争力。" },
    ],
    processTitle: "我们的流程",
    processIntro: "QbD Group 提供量身定制的技术转移服务，帮助您简化并加速过渡。无论您需要由 QbD 全程主导规划、执行与文档的交钥匙解决方案，还是由您的团队负责项目管理而由 QbD 承担技术转移活动的混合模式，我们都将提供专业能力与支持，确保流程顺畅。",
    processSteps: [
      { title: "阶段 1：角色与职责", text: "我们首先进行项目筹备，处理法律与组织方面的要求。明确界定角色与职责，为成功奠定基础。" },
      { title: "阶段 2：数据交换", text: "各方共同确定需要交换的信息。QbD 提供技术转移检查清单、模板与量身定制的建议，确保数据交换顺畅。" },
      { title: "阶段 3：工艺测试与验证", text: "接收方分析提供方的工艺，使其与内部程序及设备保持一致，并进行验证。QbD 的验证专家全程监督工艺的测试与确认。" },
      { title: "阶段 4：注册档案与经验总结", text: "所有信息被整合到注册档案中，QbD 的检查清单涵盖关键要点。审查法规合规情况并收集经验总结，以改进未来的流程。" },
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "当您与 QbD Group 合作开展技术转移时，您将获得：",
    partnerBullets: [
      "专业项目管理：清晰务实的指导，确保流程顺畅、合规执行。",
      "成本效益：通过结构化的流程和成熟的方法论，最大限度减少延误并降低成本。",
      "法规专长：确保符合法规要求，避免过渡过程中的常见陷阱。",
      "量身定制的解决方案：灵活的方式以契合您的具体需求，无论是并购、外包还是分拆。",
      "一次做对的方法：效率、精确与优化是我们服务交付的核心。",
    ],
  },
  "utility-qualification": {
    whatTitle: "什么是 utility 确认？",
    whatParagraphs: [
      "Utility 确认是验证并确保制药生产中所用的辅助系统（如水、蒸汽、空气和气体）满足所需的安全性、可靠性和性能标准的过程。",
      "在制药行业，utilities 通常被视为 GMP 合规的一部分，必须经过严格确认，以确保不会对产品质量造成不利影响或污染。",
      "需要进行确认的关键 utility 系统包括：<strong class='text-foreground'>水系统</strong>（纯化水、注射用水）、<strong class='text-foreground'>空气与气体系统</strong>（压缩空气、氮气、氧气）、<strong class='text-foreground'>HVAC 系统</strong>以及<strong class='text-foreground'>蒸汽系统</strong>。",
    ],
    whyTitle: "为什么 utility 确认很重要？",
    whyIntro: "Utility 系统在制药生产中发挥着至关重要的作用。以下是确认必不可少的原因：",
    whyBenefits: [
      { title: "法规合规", text: "确保符合 GMP 标准，避免在检查期间出现中断。" },
      { title: "产品安全与质量", text: "通过防止污染并确保 utilities 满足性能标准，维护产品的完整性。" },
      { title: "节约成本", text: "降低故障、停机及安全事故的风险，最大限度地减少代价高昂的中断。" },
      { title: "运营效率", text: "优化 utility 系统的性能，减少浪费并提升可靠性。" },
    ],
    processTitle: "我们的流程",
    processIntro: "QbD Group 提供灵活且可扩展的 utility 确认服务，根据您的项目需求量身定制。无论您需要由 QbD 负责从规划、执行到文档的完整交钥匙解决方案，还是混合模式，我们都将提供专业能力与适应性来满足您的需求。",
    processSteps: [
      { title: "第 1 步：规划", text: "我们首先进行初步咨询，明确目标、范围和需求。制定确认计划，确定时间表与交付物，确保符合法规预期。" },
      { title: "第 2 步：执行", text: "我们的团队对 utility 系统进行现场评估和功能测试，通过严格的检查与测量验证其是否符合性能标准。" },
      { title: "第 3 步：文档", text: "编制详细报告，记录测试方案、结果及任何观察事项。所有记录均能证明对法规标准的遵循。" },
      { title: "第 4 步：最终审查与验证", text: "我们审查发现，弥补任何差距，并完成确认流程。必要时提出纠正措施，以满足所需标准。" },
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "当您选择 QbD Group 进行 utility 确认时，您将获得：",
    partnerBullets: [
      "专业项目管理：清晰务实的指导，确保按时执行与合规。",
      "成本效益：通过最大限度降低风险并优化流程，降低整体项目成本。",
      "法规专长：确保完全符合 GMP 标准，避免运营中断。",
      "卓越运营：通过经过验证的 utility 系统，最大化效率与可靠性。",
      "灵活的支持模式：根据您的具体需求，从交钥匙解决方案到混合模式中自由选择。",
    ],
  },
};





/**
 * Apply localized text overrides to an OfferingSection, preserving icons/images from the original.
 */
export const getLocalizedQVOfferingData = (
  contentKey: string,
  originalData: OfferingSection,
  lang: SiteLang
): OfferingSection => {
  const override =
    lang === "es" ? offeringContentES[contentKey] :
    lang === "zh-cn" ? offeringContentZH[contentKey] :
    undefined;
  if (!override) return originalData;

  return {
    ...originalData,
    whatTitle: override.whatTitle,
    whatParagraphs: override.whatParagraphs,
    whyTitle: override.whyTitle,
    whyIntro: override.whyIntro,
    whyBenefits: originalData.whyBenefits.map((b, i) =>
      override.whyBenefits[i]
        ? { ...b, title: override.whyBenefits[i].title, text: override.whyBenefits[i].text }
        : b
    ),
    ...(override.processTitle !== undefined ? { processTitle: override.processTitle } : {}),
    ...(override.processIntro !== undefined ? { processIntro: override.processIntro } : {}),
    ...(override.processSteps && originalData.processSteps
      ? {
          processSteps: originalData.processSteps.map((s, i) =>
            override.processSteps![i]
              ? { ...s, title: override.processSteps![i].title, text: override.processSteps![i].text }
              : s
          ),
        }
      : {}),
    partnerTitle: override.partnerTitle,
    partnerIntro: override.partnerIntro,
    partnerBullets: override.partnerBullets,
  };
};

/** UI chrome for Q&V offering pages */
const qvOfferingUI = {
  en: {
    faqTitle: "Frequently Asked Questions",
    qualVal: "Qualification & Validation",
  },
  es: {
    faqTitle: "Preguntas frecuentes",
    qualVal: "Qualification & Validation",
  },
  "zh-cn": {
    faqTitle: "常见问题",
    qualVal: "Qualification & Validation",
  },
};

export const getQVOfferingUI = (lang: SiteLang) => qvOfferingUI[lang] ?? qvOfferingUI.en;

